import type { StatusTone } from "../data-display/StatusBadge";

/**
 * Campaigns & segmentation model. Segments hold a declarative jsonb filter
 * over CRM contact data (matched server-side by private.segment_matches);
 * campaigns send through the consent-gated compose_message pipeline, * refusals surface per recipient as "suppressed", never silently dropped.
 */

export interface SegmentDefinition {
  lifecycle_stages?: string[];
  sources?: string[];
  tag_ids?: number[];
  cities?: string[];
}

export interface Segment {
  id: string;
  workspace_id: string;
  name: string;
  description: string | null;
  definition: SegmentDefinition;
  created_at: string;
  updated_at: string;
}

export type CampaignChannel = "email" | "sms";

export type CampaignStatus =
  | "draft"
  | "scheduled"
  | "sending"
  | "sent"
  | "cancelled";

export interface Campaign {
  id: string;
  workspace_id: string;
  name: string;
  channel: CampaignChannel;
  subject: string | null;
  body: string;
  body_blocks?: unknown;
  variant_subject?: string | null;
  variant_body?: string | null;
  segment_id: string | null;
  folder?: string | null;
  status: CampaignStatus;
  scheduled_at: string | null;
  sent_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CampaignRecipient {
  id: string;
  campaign_id: string;
  contact_id: number;
  status: string;
  error: string | null;
  created_at: string;
}

export interface SegmentPreviewRow {
  contact_id: number;
  first_name: string | null;
  last_name: string | null;
  city: string | null;
  lifecycle_stage: string;
  eligible: boolean;
  block_reason: string | null;
}

export interface SendCampaignResult {
  campaign_id: string;
  recipients: number;
  queued: number;
  suppressed: number;
  failed: number;
  logged: number;
}

export const CAMPAIGN_STATUS_TONES: Record<CampaignStatus, StatusTone> = {
  draft: "neutral",
  scheduled: "info",
  sending: "info",
  sent: "success",
  cancelled: "warning",
};

export const RECIPIENT_STATUS_TONES: Record<string, StatusTone> = {
  pending: "neutral",
  queued: "info",
  logged: "success",
  suppressed: "warning",
  failed: "danger",
};

export const TEMPLATE_TOKEN_HINT =
  "Tokens: {{first_name}}, {{last_name}}, {{company}}";

export const emptyDefinition = (): SegmentDefinition => ({
  lifecycle_stages: [],
  sources: [],
  tag_ids: [],
  cities: [],
});

export const describeDefinition = (definition: SegmentDefinition): string => {
  const parts: string[] = [];
  if (definition.lifecycle_stages?.length)
    parts.push(`${definition.lifecycle_stages.length} stage(s)`);
  if (definition.sources?.length)
    parts.push(`${definition.sources.length} source(s)`);
  if (definition.tag_ids?.length)
    parts.push(`${definition.tag_ids.length} tag(s)`);
  if (definition.cities?.length)
    parts.push(`${definition.cities.length} city(ies)`);
  return parts.length ? parts.join(" · ") : "All contacts";
};
